import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { AssetFleetMembershipCard } from '@/components/asset/AssetFleetMembershipCard';
import assetService from '@/services/asset.service';
import pmvService from '@/services/pmv.service';
import { useAuthStore } from '@/store/authStore';
import { useProjects } from '@/hooks/useProjects';
import { buildProjectIdToLabelMap, resolveProjectDisplayFromMap } from '@/utils/pmvDisplay';

const { Text } = Typography;

const STATUS_COLOR: Record<string, string> = {
  draft: 'default',
  active: 'green',
  in_use: 'blue',
  under_maintenance: 'orange',
  idle: 'gold',
  disposed: 'red',
};

/**
 * Read-oriented Asset view with clear Fleet membership.
 * Edit remains at /asset/assets/:id/edit.
 */
const AssetViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canEdit = Boolean(user?.is_super_admin || user?.permissions?.includes('asset.edit'));
  const canManagePmv = Boolean(
    user?.is_super_admin ||
      user?.permissions?.includes('pmv.profile.manage') ||
      user?.permissions?.includes('pmv.admin') ||
      user?.permissions?.includes('pmv.manage')
  );
  const { data: projects = [] } = useProjects();
  const projectLabels = useMemo(() => buildProjectIdToLabelMap(projects), [projects]);

  const assetQ = useQuery({
    queryKey: ['asset', id],
    queryFn: () => assetService.getById(id!),
    enabled: Boolean(id),
  });

  const pmvQ = useQuery({
    queryKey: ['pmv-profile-by-asset', id],
    queryFn: () => pmvService.getProfileByAsset(id!),
    enabled: Boolean(id) && canManagePmv,
    retry: false,
  });

  const wsQ = useQuery({
    queryKey: ['pmv-workspace-asset', pmvQ.data?.id],
    queryFn: () => pmvService.getWorkspace(pmvQ.data!.id),
    enabled: Boolean(pmvQ.data?.id),
  });

  const capitalizeMut = useMutation({
    mutationFn: () => assetService.capitalize(id!),
    onSuccess: (res) => {
      message.success(`Capitalized. Journal ${res.journal_voucher_number}`);
      void queryClient.invalidateQueries({ queryKey: ['asset', id] });
      void queryClient.invalidateQueries({ queryKey: ['assets'] });
    },
    onError: (e: Error) => message.error(e.message || 'Capitalization failed'),
  });

  const asset = assetQ.data;
  const alloc = (wsQ.data as { operational?: { allocation?: { state?: string; project_id?: string; planned_end?: string } } })
    ?.operational?.allocation;

  if (assetQ.isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!asset) {
    return <Empty description="Asset not found" />;
  }

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Asset"
          documentNumber={asset.asset_code}
          statusTag={
            <Tag color={STATUS_COLOR[asset.status] || 'default'}>{asset.status?.replace(/_/g, ' ')}</Tag>
          }
          subtitle={<Text type="secondary">{asset.asset_name}</Text>}
          primaryActions={
            <Space wrap>
              {canEdit && asset.status === 'draft' && !asset.capitalization_journal_voucher_id && (
                <Button
                  type="primary"
                  loading={capitalizeMut.isPending}
                  onClick={() => capitalizeMut.mutate()}
                >
                  Activate / Capitalize
                </Button>
              )}
              {canEdit && (
                <Button type="primary" icon={<EditOutlined />} onClick={() => navigate(`/asset/assets/${id}/edit`)}>
                  Edit asset
                </Button>
              )}
              <Button onClick={() => navigate('/asset/assets')}>Asset register</Button>
              <Button onClick={() => navigate(`/asset/maintenance?asset=${id}`)}>Maintenance</Button>
            </Space>
          }
        />
      }
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <AssetFleetMembershipCard
          assetId={id!}
          assetCode={asset.asset_code}
          assetName={asset.asset_name}
          disposed={asset.status === 'disposed'}
          canManagePmv={canManagePmv}
          loading={pmvQ.isLoading}
          profile={pmvQ.data}
          profileLoaded={pmvQ.isFetched}
          projectLabel={
            alloc?.state === 'ACTIVE'
              ? resolveProjectDisplayFromMap(projectLabels, alloc.project_id)
              : null
          }
          expectedRelease={alloc?.state === 'ACTIVE' ? alloc.planned_end ?? null : null}
        />

        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Card size="small" title="Identity">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Code">{asset.asset_code}</Descriptions.Item>
                <Descriptions.Item label="Name">{asset.asset_name}</Descriptions.Item>
                <Descriptions.Item label="Category">{asset.asset_category || '—'}</Descriptions.Item>
                <Descriptions.Item label="Manufacturer">{asset.manufacturer || '—'}</Descriptions.Item>
                <Descriptions.Item label="Model">{asset.model || '—'}</Descriptions.Item>
                <Descriptions.Item label="Serial">{asset.serial_number || '—'}</Descriptions.Item>
                <Descriptions.Item label="Barcode">{asset.barcode || '—'}</Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card size="small" title="Location & custody">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Site">{asset.site || '—'}</Descriptions.Item>
                <Descriptions.Item label="Building">{asset.building || '—'}</Descriptions.Item>
                <Descriptions.Item label="Room">{asset.room || '—'}</Descriptions.Item>
                <Descriptions.Item label="Book value">
                  {asset.current_book_value != null ? Number(asset.current_book_value).toLocaleString() : '—'}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        </Row>
      </Space>
    </WorkspaceLayout>
  );
};

export default AssetViewPage;
